/**
 * Modal camera interface used to capture photos.
 * Props: open flag, onClose and onImageTaken callbacks.
 */
import { useEffect, useRef } from 'react';
import { Modal, Box, Button, IconButton } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloseIcon from '@mui/icons-material/Close';
import { useConfigurations } from '../../../context/ConfigurationsContext/ConfigurationsContext';
interface CameraViewerProps {
  open: boolean;
  onClose: () => void;
  onImageTaken: (file: File) => void;
  closeOnCapture?: boolean;
}

function CameraViewer({ open, onClose, onImageTaken, closeOnCapture = true }: CameraViewerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const{setCameraActive}=useConfigurations();
  
  useEffect(() => {
    if (open && navigator.mediaDevices?.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({ video: { facingMode: 'environment' } })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
        })
        .catch((error) =>
          console.error('Error accessing media devices.', error)
        );
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, [open]);


  useEffect(() => {
    setCameraActive(open);
    return () => {
      setCameraActive(false);
    };
  }, [open, setCameraActive]);

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;

    if (canvas && video) {
      const context = canvas.getContext('2d');
      if (!context) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
          onImageTaken(file);
          if (closeOnCapture) onClose();
        }
      }, 'image/jpeg');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1700 }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100vw',
          height: '100vh',
          bgcolor: 'black',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 10, right: 10, color: 'white' }}
        >
          <CloseIcon />
        </IconButton>
        <video
          ref={videoRef}
          autoPlay
          style={{ width: '90%', height: 'auto', maxHeight: '80vh' }}
        />
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        {/* Botón de captura en el lado derecho ocupando todo el alto */}
        <Button
          onClick={capturePhoto}
          variant="contained"
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            borderRadius: 0,
            padding: 0,
            minWidth: '60px',
          }}
        >
          <CameraAltIcon sx={{ fontSize: '3rem' }} />
        </Button>
        {/* Botón de cancelar flotante */}
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            position: 'absolute',
            bottom: 20,
            left: 20,
            color: 'white',
            borderColor: 'white',
          }}
        >
          Cancelar
        </Button>
      </Box>
    </Modal>
  );
}

export default CameraViewer;
